/**
 * マルチ端末同期の SSE イベント型定義とディスパッチャ.
 *
 * バックエンド `api/routes/sync.py` から `text/event-stream` で配信される
 * JSON イベントのスキーマ。`origin` は変更を行った端末の `X-Client-Id`。
 * 自端末発のイベントは無視する（楽観的更新済のため）。
 */

export type SyncEvent =
  | { type: 'hello' }
  | { type: 'ping' }
  // TODO
  | { type: 'todo.created'; id: number; origin?: string }
  | { type: 'todo.updated'; id: number; origin?: string }
  | { type: 'todo.deleted'; id: number; origin?: string }
  // 栄養
  | { type: 'nutrition.meal.created'; id: number; date?: string; origin?: string }
  | { type: 'nutrition.meal.updated'; id: number; date?: string; origin?: string }
  | { type: 'nutrition.meal.deleted'; id: number; date?: string; origin?: string }
  | { type: 'nutrition.profile.updated'; origin?: string }
  | { type: 'nutrition.template.created'; id: number; origin?: string }
  | { type: 'nutrition.template.updated'; id: number; origin?: string }
  | { type: 'nutrition.template.deleted'; id: number; origin?: string }
  | { type: 'meal_plan.updated'; origin?: string }
  // 個人データ
  | { type: 'workout.session.changed'; id?: number; origin?: string }
  | { type: 'workout.template.changed'; id?: number; origin?: string }
  | { type: 'workout.exercise.changed'; id?: number; origin?: string }
  | { type: 'workout.profile.changed'; origin?: string }
  | { type: 'memo.created'; id: number; origin?: string }
  | { type: 'memo.updated'; id: number; origin?: string }
  | { type: 'memo.deleted'; id: number; origin?: string }
  | { type: 'flashcard.created'; id: number; origin?: string }
  | { type: 'flashcard.updated'; id: number; origin?: string }
  | { type: 'flashcard.deleted'; id: number; origin?: string }
  // 図書館
  | { type: 'library_favorite.created'; id: number; origin?: string }
  | { type: 'library_favorite.updated'; id: number; origin?: string }
  | { type: 'library_favorite.deleted'; id: number; origin?: string }
  | { type: 'library_selection.updated'; origin?: string }
  // アクティビティ（Visit・位置データ）。dates が無ければ全日付が対象
  | { type: 'activity.changed'; dates?: string[]; origin?: string }
  // ダウンロードキュー
  | { type: 'download_queue.updated'; id?: number; origin?: string }
  // PDF 再 OCR
  | {
      type: 'ocr.updated'
      id: number
      product_id: number
      status?: string
      progress?: number
      processed_pages?: number
      total_pages?: number
      message?: string
      origin?: string
    }
  // NotebookLM 取り込み
  | {
      type: 'notebooklm.updated'
      id: number
      product_id: number
      product_title?: string | null
      status?: string
      progress?: number
      processed_sources?: number
      total_sources?: number
      notebook_url?: string
      unlinked?: boolean
      message?: string
      origin?: string
    }

export type SyncTopic =
  | 'todo'
  | 'nutrition'
  | 'meal_plan'
  | 'workout'
  | 'memo'
  | 'flashcard'
  | 'library_favorite'
  | 'library_selection'
  | 'download_queue'
  | 'ocr'
  | 'activity'

/** 各 Zustand store が実装するリモートイベント適用関数の型 */
export type ApplyRemoteEvent = (event: SyncEvent) => void

const listeners = new Set<ApplyRemoteEvent>()

/**
 * ストアから applyRemoteEvent 相当のハンドラを登録する。
 * 通常はモジュールロード時に一度だけ呼び出す。
 */
export const registerSyncListener = (fn: ApplyRemoteEvent): (() => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** 受信イベントを全リスナーへディスパッチ */
export const dispatchSyncEvent = (event: SyncEvent): void => {
  for (const listener of listeners) {
    try {
      listener(event)
    } catch (err) {
      console.error('[sync] listener failed:', err)
    }
  }
}
